import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAssistantUserAutoCompleteValueModel } from '@nimbus-assistant/interfaces/assistant-user-autocomplete-value-model';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { CallerIdentifier } from '@nimbus-assistant/models/task-attributes.model';
import { IdentifierType } from '@nimbus-assistant/enums/identifier-type';
import { IdentifierTypeMapper } from '@nimbus-assistant/models/mappers/identifier-type.mapper';
import {
  CallTransfer,
  CallTransferRequest,
  ConsultationCallToServiceRequest,
  ConsultationType,
  TransferState,
} from '@nimbus-assistant/models/call-transfer';
import { TransferType } from '@nimbus-assistant/enums/transfer-type';
import { TransferRequestType } from '@nimbus-assistant/enums/transfer-request-type';
import { AppRoutes } from '@nimbus-assistant/enums/app-routes';
import { TaskState, TaskConversationState, ITask } from '@nimbus-assistant/models/task.model';
import { UserAutoCompleteValueType } from '@nimbus-library/features/shared/services/user-search/models/user-auto-complete-value-type';
import {
  ConsultationDirection,
  ConsultationSessionState,
} from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IErrorStatus } from '@nimbus-library/features/shared/models/interfaces/i-error-status.model';
import { ApiService } from './api/api.service';
import { LoggingService } from './logging.service';
import { SettingsService } from './settings.service';
import { UserSettingsService } from './user-settings.service';
import { ServicesService } from './services.service';
import { MessageService } from './message.service';
import { AssistantTasksService } from './assistant-tasks.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class TaskTransferService {
  private readonly showTransferResultTime = 5000;
  private readonly consultationCallToServiceNotSupportedCode = 'consultation_call_to_service_not_supported';
  private readonly safeTransferToServiceNotSupportedCode = 'safe_transfer_to_service_not_supported';
  private readonly transferToServiceWithoutPstnCode = 'transfer_to_service_without_pstn';
  private readonly transferTypesBlockedForNimbusUnifyService = [TransferType.consultativeTransfer, TransferType.safeTransfer];

  private showTransferResultFor: string[] = [];
  private isInConsultativeCall = false;
  public callTransfer$ = new BehaviorSubject<CallTransfer>(null);

  public showTransferBar$ = combineLatest([this.tasksService.connectedTask$, this.callTransfer$]).pipe(
    map(([task, transfer]) => {
      if (!task) {
        return false;
      }

      const consultationSession = task.consultationSession;
      if (
        consultationSession?.currentState === ConsultationSessionState.Starting &&
        consultationSession?.direction === ConsultationDirection.Target &&
        this.userSettingsService.userSettings.onTopTeamToast
      ) {
        return false;
      }

      const transferInitiated = transfer?.state && transfer.state !== TransferState.Initial;
      const isInTransfer = this.taskIsInTransfer(task);
      const isOnHold = task.state === TaskState.Connected && task.conversationState === TaskConversationState.PutOnHold;
      const showingTransferResult = !!this.showTransferResultFor.length;

      return transferInitiated || isInTransfer || isOnHold || showingTransferResult;
    }),
  );

  public get transferType(): TransferType | null {
    return this.callTransfer$.value?.type || null;
  }

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly apiService: ApiService,
    private readonly loggingService: LoggingService,
    private readonly settingsService: SettingsService,
    private readonly userSettingsService: UserSettingsService,
    private readonly servicesService: ServicesService,
    private readonly router: Router,
  ) {
    this.watchConnectedTask();
    this.watchConsultativeResize();
  }

  public isTransferToServiceBlocked(target: string): boolean {
    return (
      (this.transferTypesBlockedForNimbusUnifyService.includes(this.transferType) &&
        this.servicesService.isNimbusUnifyServiceTarget(target)) ||
      this.isBlindTransferToServiceWithoutPstn(target)
    );
  }

  public getTransferToServiceBlockedReason(target: string): TranslationKeys {
    return this.servicesService.isNimbusUnifyServiceWithoutPstn(target)
      ? TranslationKeys.ContactActionTransferNotAvailableForServiceWithoutPstn
      : TranslationKeys.AssistantOutboundOnlyBlindTransferAvailableForService;
  }

  // --- Initiate transfer (navigates to outbound dialer) ---

  public processTransfer(callTransfer: CallTransfer): void {
    this.callTransfer$.next(callTransfer);
    this.router.navigate([AppRoutes.outbound]);
  }

  // --- Execute transfer (called from outbound component after user picks target) ---

  public transferCall(transfer: CallTransfer, targetOption: IAssistantUserAutoCompleteValueModel): Observable<void> {
    this.loggingService.Info(`Initiating transfer ${JSON.stringify(this.callTransfer$.value)}`);

    const target = targetOption.value.replaceAll(' ', '');
    this.callTransfer$.next({
      ...this.callTransfer$.value,
      target,
      state: TransferState.Transferring,
    });

    const task = this.getConnectedTask();
    const sourceServiceName = task?.teamInfo?.upn;
    const transferCall = this.getTransfer(transfer, targetOption, sourceServiceName);

    return transferCall.pipe(catchError((httpResponse) => this.handleTransferError(transfer, httpResponse)));
  }

  // --- Start consultation directly (consult: link, no dialer) ---

  /** Not marked in progress up front: the footer waits for the backend's consultation session. */
  public startConsultation(transfer: CallTransfer, targetOption: IAssistantUserAutoCompleteValueModel): Observable<void> {
    this.loggingService.Info(`Initiating consultation ${JSON.stringify(transfer)}`);

    const sourceServiceName = this.getConnectedTask()?.teamInfo?.upn;
    return this.getTransfer(transfer, targetOption, sourceServiceName).pipe(
      catchError((httpResponse) => {
        this.callTransfer$.next(transfer);
        return this.handleTransferError(transfer, httpResponse);
      }),
    );
  }

  // --- Hold / Resume ---

  public hold(): Observable<void> {
    const task = this.getConnectedTask();
    const o365Id = this.settingsService.settings$.getValue().office365Id;

    if (!task?.unifiedConversationId || !o365Id) {
      this.loggingService.Error('Missing required parameters for hold');
      return of(null);
    }

    this.loggingService.Info(`Holding ${task.unifiedConversationId}`);
    return this.apiService.hold(task.unifiedConversationId, o365Id).pipe(
      catchError((httpResponse) => {
        this.loggingService.Error(`Hold failed: ${JSON.stringify(httpResponse)}`);
        return of(null);
      }),
    );
  }

  public resume(): Observable<void> {
    const task = this.getConnectedTask();
    const o365Id = this.settingsService.settings$.getValue().office365Id;

    if (!task?.unifiedConversationId || !o365Id) {
      this.loggingService.Error('Missing required parameters for resume');
      return of(null);
    }

    this.loggingService.Info(`Resuming ${task.unifiedConversationId}`);
    return this.apiService.resume(task.unifiedConversationId, o365Id).pipe(
      catchError((httpResponse) => {
        this.loggingService.Error(`Resume failed: ${JSON.stringify(httpResponse)}`);
        return of(null);
      }),
    );
  }

  // --- Consultation operations ---

  public mergeConsultationCall(): Observable<void> {
    return this.executeConsultationAction('Merging', (task, o365Id, consultationCallId, isService) =>
      isService
        ? this.apiService.mergeConsultationCallToService(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId)
        : this.apiService.mergeConsultationCall(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId),
    );
  }

  public swapConsultationCall(): Observable<void> {
    return this.executeConsultationAction('Swapping', (task, o365Id, consultationCallId, isService) =>
      isService
        ? this.apiService.swapConsultationCallToService(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId)
        : this.apiService.swapConsultationCall(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId),
    );
  }

  public transferConsultationCall(): Observable<void> {
    return this.executeConsultationAction('Transferring', (task, o365Id, consultationCallId, isService) =>
      isService
        ? this.apiService.transferConsultationCallToService(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId)
        : this.apiService.transferConsultationCall(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId),
    );
  }

  public terminateConsultationCall(): Observable<void> {
    const task = this.getConnectedTask();
    const o365Id = this.settingsService.settings$.getValue().office365Id;
    const consultation = task?.consultationSession;
    const isConsultant = consultation?.direction === ConsultationDirection.Target;

    if (!task?.unifiedConversationId || !task?.conversationId || !o365Id) {
      this.loggingService.Error('Missing required parameters for terminate consultation call');
      return of(null);
    }

    if (isConsultant) {
      this.loggingService.Info('Terminating consultation session as consultant');
      return this.apiService.terminateSession(task.unifiedConversationId, task.conversationId, Modality.Audio).pipe(
        catchError((httpResponse) => {
          this.loggingService.Error(`Terminate consultation session failed: ${JSON.stringify(httpResponse)}`);
          return of(null);
        }),
      );
    }

    const consultationCallId = consultation?.id;
    if (!consultationCallId) {
      this.loggingService.Error('Missing consultationCallId for agent terminate consultation call');
      return of(null);
    }

    const isService = consultation?.consultationType === ConsultationType.Service;
    this.loggingService.Info(`Terminating consultation call ${consultationCallId}`);

    const request = isService
      ? this.apiService.terminateConsultationCallToService(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId)
      : this.apiService.terminateConsultationCall(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId);

    return request.pipe(
      catchError((httpResponse) => {
        this.loggingService.Error(`Terminate consultation call failed: ${JSON.stringify(httpResponse)}`);
        return of(null);
      }),
    );
  }

  public terminateConsultationCallAndResume(): Observable<void> {
    return this.executeConsultationAction('Terminating and resuming', (task, o365Id, consultationCallId, isService) =>
      isService
        ? this.apiService.terminateConsultationCallToServiceAndResume(
            task.unifiedConversationId,
            task.conversationId,
            o365Id,
            consultationCallId,
          )
        : this.apiService.terminateConsultationCallAndResume(task.unifiedConversationId, task.conversationId, o365Id, consultationCallId),
    );
  }

  public terminateCall(): Observable<void> {
    const task = this.getConnectedTask();
    if (!task?.unifiedConversationId || !task?.conversationId) {
      this.loggingService.Error('Missing required parameters for terminate call');
      return of(null);
    }

    this.loggingService.Info(`Terminating session ${task.conversationId} of call ${task.unifiedConversationId}`);
    return this.apiService.terminateSession(task.unifiedConversationId, task.conversationId, Modality.Audio).pipe(
      catchError((httpResponse) => {
        this.loggingService.Error(`Terminate session failed: ${JSON.stringify(httpResponse)}`);
        return of(null);
      }),
    );
  }

  public cleanup(unifiedConversationId: string = null): void {
    if (unifiedConversationId) {
      this.showTransferResultFor = this.showTransferResultFor.filter((id) => id !== unifiedConversationId);
      if (this.callTransfer$.value?.unifiedConversationId === unifiedConversationId) {
        this.callTransfer$.next(null);
      }
    } else {
      this.showTransferResultFor = [];
      this.callTransfer$.next(null);
    }
  }

  public resizeWindowForCurrentCall(): void {
    const height = this.isInConsultativeCall ? this.userSettingsService.defaultIncreasedHeight : this.userSettingsService.defaultHeight;
    MessageService.SendResizeWindow(this.userSettingsService.defaultWidth, height);
  }

  // --- Internal: watch connected task for transfer state changes ---

  private watchConnectedTask(): void {
    let previousTask: ITask | null = null;
    this.tasksService.connectedTask$.pipe(untilDestroyed(this)).subscribe((current) => {
      this.watchTransferCompletion(current, previousTask);
      this.watchTransferState(current);
      previousTask = current;
    });

    // Cleanup any existing transfer when a new ringing session appears
    this.tasksService.incomingTask$
      .pipe(
        distinctUntilChanged((a, b) => a?.unifiedConversationId === b?.unifiedConversationId),
        filter((incoming) => !!incoming),
        untilDestroyed(this),
      )
      .subscribe(() => this.cleanup());
  }

  private watchTransferCompletion(task: ITask | null, previousTask: ITask | null): void {
    if (!task || !this.taskIsInTransfer(task)) {
      if (previousTask && this.taskIsTransferring(previousTask)) {
        const transferState =
          task?.conversationState === TaskConversationState.Terminated || task?.conversationState === TaskConversationState.InAfterCallWork
            ? TransferState.TransferSuccessful
            : TransferState.TransferFailed;
        const errorMessage =
          transferState === TransferState.TransferFailed ? TranslationKeys.AssistantSessionFooterConversationStateTransferFailed : null;
        this.handleTransferEnd(previousTask.unifiedConversationId, transferState, errorMessage);
      } else if (
        this.callTransfer$.value?.state === TransferState.InConsultativeTransfer ||
        (this.callTransfer$.value?.state === TransferState.Transferring &&
          this.callTransfer$.value?.type !== TransferType.consultativeTransfer)
      ) {
        this.cleanup();
      }
    }
  }

  private watchTransferState(task: ITask | null): void {
    if (!task) {
      return;
    }

    const receivedTransferringState = task.conversationState === TaskConversationState.Transferring;
    const inConsultativeCall = this.taskIsInConsultativeCall(task);

    if (receivedTransferringState || inConsultativeCall) {
      const state = inConsultativeCall ? TransferState.InConsultativeTransfer : TransferState.Transferring;
      const callerName = this.callTransfer$.value?.callerName || task.caller?.displayName;

      this.callTransfer$.next({
        ...this.callTransfer$.value,
        unifiedConversationId: task.unifiedConversationId,
        conversationId: task.conversationId,
        state,
        callerName,
      });
    }
  }

  // --- Internal: helpers ---

  private isBlindTransferToServiceWithoutPstn(target: string): boolean {
    return this.transferType === TransferType.blindTransfer && this.servicesService.isNimbusUnifyServiceWithoutPstn(target);
  }

  private getConnectedTask(): ITask | null {
    return this.tasksService.connectedTaskSnapshot;
  }

  private getTransfer(
    transfer: CallTransfer,
    targetOption: IAssistantUserAutoCompleteValueModel,
    sourceServiceName?: string,
  ): Observable<void> {
    const target = targetOption.value.replaceAll(' ', '');
    const o365Id = this.settingsService.settings$.getValue().office365Id;

    if (this.servicesService.isNimbusUnifyServiceWithoutPstn(target)) {
      this.loggingService.Info('Transfer to a Nimbus Unify service without a PSTN number is not supported');
      return throwError(() => ({ error: { Code: this.transferToServiceWithoutPstnCode } }) as IErrorStatus);
    }

    if (transfer.type === TransferType.consultativeTransfer) {
      if (this.servicesService.isNimbusUnifyServiceTarget(target)) {
        this.loggingService.Info('Consultation to a Nimbus Unify service is not supported');
        return throwError(() => ({ error: { Code: this.consultationCallToServiceNotSupportedCode } }) as IErrorStatus);
      }
      if (targetOption.isService) {
        const serviceMapping = this.servicesService.serviceApplicationMappings$?.value?.find((service) => service.upn === target);
        if (!serviceMapping?.teamId) {
          this.loggingService.Error(`Service mapping not found for UPN: ${target}`);
          // Handled upwards by the callers' catchError.
          return throwError(() => null);
        }
        const serviceRequest: ConsultationCallToServiceRequest = {
          targetTeamId: serviceMapping.teamId,
          sourceServiceName: sourceServiceName || '',
        };
        return this.apiService.consultationCallToService(transfer.unifiedConversationId, transfer.conversationId, o365Id, serviceRequest);
      } else {
        const callerIdentifier = new CallerIdentifier(target, IdentifierTypeMapper.map(targetOption.type), transfer.transferTargetName);
        const request = new CallTransferRequest(callerIdentifier, TransferRequestType.Default, transfer.serviceName, transfer.callerName);
        request.consultationType = ConsultationType.User;
        return this.apiService.consultativeTransfer(transfer.unifiedConversationId, transfer.conversationId, o365Id, request);
      }
    }

    const callerIdentifier = targetOption.isService
      ? new CallerIdentifier(target, IdentifierType.Guid, transfer.transferTargetName)
      : new CallerIdentifier(target, IdentifierTypeMapper.map(targetOption.type), transfer.transferTargetName);

    const request = new CallTransferRequest(callerIdentifier, TransferRequestType.Default, transfer.serviceName, transfer.callerName);
    const isServiceTransfer = targetOption.isService && targetOption.type === UserAutoCompleteValueType.GUID;

    if (transfer.type === TransferType.safeTransfer) {
      if (this.servicesService.isNimbusUnifyServiceTarget(target)) {
        this.loggingService.Info('Safe transfer to a Nimbus Unify service is not supported');
        return throwError(() => ({ error: { Code: this.safeTransferToServiceNotSupportedCode } }) as IErrorStatus);
      }
      return isServiceTransfer
        ? this.apiService.safeTransferToService(transfer.unifiedConversationId, transfer.conversationId, transfer.modality, target)
        : this.apiService.safeTransfer(transfer.unifiedConversationId, transfer.conversationId, transfer.modality, request);
    }

    if (transfer.type === TransferType.blindTransfer) {
      return isServiceTransfer
        ? this.apiService.blindTransferToService(transfer.unifiedConversationId, transfer.conversationId, transfer.modality, target)
        : this.apiService.blindTransfer(transfer.unifiedConversationId, transfer.conversationId, transfer.modality, request);
    }

    return of(null);
  }

  private handleTransferError(transfer: CallTransfer, httpResponse: IErrorStatus | null): Observable<void> {
    this.loggingService.Info(`Transfer (${transfer.type}) failed: ${JSON.stringify(httpResponse)}`);
    const errorMessage = httpResponse?.error?.Exception?.Message;
    let message = TranslationKeys.AssistantSessionFooterConversationStateTransferFailed;
    if (errorMessage?.includes('TM.InNimbusTask')) {
      message = TranslationKeys.GeneralAttendantConsoleErrorsTransferTargetBusy;
    }
    if (errorMessage?.includes('TM.OfflineOrDnD')) {
      message = TranslationKeys.GeneralAttendantConsoleErrorsTransferTargetDnd;
    }
    if (
      httpResponse?.error?.Code === this.consultationCallToServiceNotSupportedCode ||
      httpResponse?.error?.Code === this.safeTransferToServiceNotSupportedCode
    ) {
      message = TranslationKeys.AssistantOutboundOnlyBlindTransferAvailableForService;
    }
    if (httpResponse?.error?.Code === this.transferToServiceWithoutPstnCode) {
      message = TranslationKeys.ContactActionTransferNotAvailableForServiceWithoutPstn;
    }
    this.handleTransferEnd(transfer.unifiedConversationId, TransferState.TransferFailed, message);
    return of(null);
  }

  private handleTransferEnd(sessionId: string, state: TransferState, errorMessage?: string): void {
    this.showTransferResultFor.push(sessionId);
    this.callTransfer$.next({
      ...this.callTransfer$.value,
      state,
      errorMessage,
    });
    setTimeout(() => {
      this.cleanup(sessionId);
    }, this.showTransferResultTime);
  }

  private executeConsultationAction(
    actionName: string,
    apiCall: (task: ITask, o365Id: string, consultationCallId: string, isService: boolean) => Observable<void>,
  ): Observable<void> {
    const task = this.getConnectedTask();
    const consultationCallId = task?.consultationSession?.id;
    const o365Id = this.settingsService.settings$.getValue().office365Id;
    const isService = task?.consultationSession?.consultationType === ConsultationType.Service;

    if (!consultationCallId || !task?.unifiedConversationId || !task?.conversationId || !o365Id) {
      this.loggingService.Error(`Missing required parameters for ${actionName} consultation call`);
      return of(null);
    }

    this.loggingService.Info(`${actionName} consultation call ${consultationCallId}`);
    return apiCall(task, o365Id, consultationCallId, isService).pipe(
      catchError((httpResponse) => {
        this.loggingService.Error(`${actionName} consultation call failed: ${JSON.stringify(httpResponse)}`);
        return of(null);
      }),
    );
  }

  private taskIsInConsultativeCall(task: ITask): boolean {
    const consultation = task?.consultationSession;
    if (!consultation) {
      return false;
    }
    if (task.state === TaskState.InACW || task.state === TaskState.InExtendedACW) {
      return false;
    }
    if ([ConsultationSessionState.Canceled, ConsultationSessionState.Completed].includes(consultation.currentState)) {
      return false;
    }
    return [ConsultationSessionState.Starting, ConsultationSessionState.Ongoing, ConsultationSessionState.PutOnHold].includes(
      consultation.currentState,
    );
  }

  private taskIsTransferring(task: ITask): boolean {
    return task?.conversationState === TaskConversationState.Transferring;
  }

  private taskIsInTransfer(task: ITask): boolean {
    return this.taskIsTransferring(task) || this.taskIsInConsultativeCall(task);
  }

  private watchConsultativeResize(): void {
    // UpdateSettings below writes the whole object: start only once the host's settings are loaded, never with the startup defaults
    this.userSettingsService.hostSettingsReceived$
      .pipe(
        filter((received) => received),
        take(1),
        switchMap(() => this.tasksService.connectedTask$),
        map(
          (task) => !!task && task.consultationSession?.direction !== ConsultationDirection.Target && this.taskIsInConsultativeCall(task),
        ),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((inConsultativeCall) => {
        this.isInConsultativeCall = inConsultativeCall;
        this.resizeWindowForCurrentCall();
        // The host grows the window upwards: UpdateSettings places it back in its corner without resetting the size
        MessageService.SendUpdateSettings(this.userSettingsService.userSettings);
      });
  }
}
